import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react';
import toast from 'react-hot-toast';
import { handleError } from '../utils/Error';
import { Server } from '../services/Server';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation as useQueryMutation } from '@tanstack/react-query';
import { useAppContext } from './AppContext';
import { coerceFieldValue } from '../utils/fieldTransforms';

type FormValues = Record<string, unknown>;

interface FormField {
  name: string;
  params?: Record<string, unknown>;
  onChange?: (value: unknown) => void;
  min?: number;
  max?: number | string;
}

interface UploadParams {
  project_id?: string;
  [key: string]: unknown;
}

interface UploadVariables {
  file: File;
  field: Partial<FormField>;
  params: UploadParams;
  name: string;
}

interface CSVUploadResponse {
  warning?: string;
  [key: string]: unknown;
}

type SoilingSensorUploadResponse = string | { file_uid?: string; warning?: string };

interface SensorFileSelection {
  uid: string;
  selected?: unknown[];
}

interface SoilingRatioSelections {
  sensor_files?: SensorFileSelection[];
  hsu_selected?: boolean;
  [key: string]: unknown;
}

interface ChangeTarget {
  name: string;
  value: unknown;
  type?: string;
  checked?: boolean;
  files?: FileList | null;
}

interface FieldChangeEvent {
  target: ChangeTarget;
  type?: string;
  checked?: boolean;
}

interface FormContextType {
  getValues: () => FormValues;
  setFormData: (data: FormValues | ((prevFormData: FormValues) => FormValues)) => void;
  getFormDataItem: <T = unknown>(data: unknown, name: string) => T;
  updateDataValue: (prevFormData: FormValues, name: string, value: unknown) => void;
  updateFormValue: (name: string, value: unknown) => void;
  results: Record<string, unknown>;
  setResults: Dispatch<SetStateAction<Record<string, unknown>>>;
  handleChange: (e: FieldChangeEvent, field: Partial<FormField>) => void;
  handleSpecialArrayFieldChange: (e: FieldChangeEvent, field: FormField, index: number) => void;
  clearSoilingRatioSelections: (ratioKey: string, fileUidToRemove?: string) => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

interface FormContextProviderProps {
  children: ReactNode;
}

const FormContextProvider: React.FC<FormContextProviderProps> = ({ children }) => {
  const { modified, setModified, role } = useAppContext();
  const { postValidateCSVAPI, postSoilingSensorDataFileAPI, postSensorDataAPI } = Server();

  const methods = useForm<FormValues>({ defaultValues: {} });
  const { getValues, setValue, reset } = methods;
  const [results, setResults] = useState<Record<string, unknown>>({});

  const updateDataValue = (prevFormData: FormValues, name: string, value: unknown) => {
    const keys = name.split('.');
    keys.reduce<Record<string, unknown>>((acc, key, index) => {
      if (!Object.prototype.hasOwnProperty.call(acc, key)) {
        acc[key] = {};
      }
      if (index === keys.length - 1) {
        acc[key] = value;
      }
      return acc[key] as Record<string, unknown>;
    }, prevFormData);
    return prevFormData;
  };

  const getFormDataItem = <T = unknown,>(data: unknown, name: string): T => {
    return name.split('.').reduce<unknown>((acc, key) => {
      return acc && Object.prototype.hasOwnProperty.call(acc, key) ? (acc as Record<string, unknown>)[key] : '';
    }, data) as T;
  };

  const setFormData = (data: FormValues | ((prevFormData: FormValues) => FormValues)) => {
    const next = typeof data === 'function' ? data(getValues()) : data;
    reset(next);
  };

  const updateFormValue = (name: string, value: unknown) => {
    setValue(name, value);
  };

  const handleChange = (e: FieldChangeEvent, field: Partial<FormField>) => {
    const { name, value, type, checked } = e.target;

    if (type === 'file') {
      const file = e.target.files?.[0];
      let params: UploadParams = {};

      if (!file) return;
      if (Object.prototype.hasOwnProperty.call(field, 'params')) {
        // Evaluate parameters that contain form data references
        params = Object.entries(field.params ?? {}).reduce<UploadParams>((acc, [key, value]) => {
          if (typeof value === 'string' && value.includes('.')) {
            // This is a reference to form data, evaluate it
            acc[key] = getFormDataItem(getValues(), value);
          } else {
            // This is a literal value, use it as is
            acc[key] = value;
          }
          return acc;
        }, {});
      }

      // Determine single_year based on role
      const singleYear = !['ex_post_analysis', 'operational_strategy', 'schedule_optimization'].includes(role);

      // Special handling for soiling sensor data uploads
      if (name === 'soiling_sensor_data.pattern' || name.match(/^soiling_sensor_data_\d+\.pattern$/)) {
        // Require project_id to be present before allowing upload
        if (!params || !params.project_id) {
          toast.error('Please save the project before uploading soiling sensor files.');
          return;
        }
        postSoilingSensorData.mutate({ file, field, params, name });
        // Don't update the pattern field with the filename for soiling sensor data
        return;
      } else {
        // Don't fall through to updateFormValue: that would store the file input's filename
        // (a string) into the target field. validateCSV.onSuccess sets the parsed
        // { dates, values, filename } object instead; on failure the previous value is kept.
        validateCSV.mutate({ file, params: { ...params, single_year: singleYear }, field, name });
        return;
      }
    }

    const val = coerceFieldValue({ name, value, type, checked }, field, (ref) =>
      typeof ref === 'string' ? getFormDataItem<number>(getValues(), ref) : ref,
    );

    updateFormValue(name, val);
    if (Object.prototype.hasOwnProperty.call(field, 'onChange')) {
      field.onChange?.(val);
    }

    // Turning an "external source" toggle off (*.from_api -> false) with no uploaded file yet means
    // the user is about to upload one. Defer the autosave until the upload lands: saving now would
    // validate the still-empty pattern and raise a spurious "please fill in the ... field" error.
    // If a valid pattern already exists, the state is valid, so save immediately as usual.
    let deferSave = false;
    if (typeof name === 'string' && name.endsWith('.from_api') && checked === false) {
      const blockName = name.slice(0, -'.from_api'.length);
      const values = getValues();
      const pattern = getFormDataItem(values, `${blockName}.rain_pattern`) ?? getFormDataItem(values, `${blockName}.pattern`);
      deferSave = !(pattern && typeof pattern !== 'string');
    }

    if (!modified && !deferSave) {
      setModified(true);
      setResults({});
    }
  };

  const handleSpecialArrayFieldChange = (e: FieldChangeEvent, field: FormField, index: number) => {
    const val = getFormDataItem<unknown[]>(getValues(), field.name);
    val[index] = e.target.value;
    handleChange({ target: { name: field.name, value: val, type: e.type, checked: e.checked } }, field);
  };

  const validateCSV = useQueryMutation({
    mutationFn: ({ file, params }: UploadVariables): Promise<CSVUploadResponse> => {
      return postValidateCSVAPI(file, params);
    },
    onSuccess: (data, variables) => {
      const { field, name } = variables;
      updateFormValue(name, data);
      if (Object.prototype.hasOwnProperty.call(field, 'onChange')) {
        field.onChange?.(data);
      }
      if (!modified) {
        setModified(true);
        setResults({});
      }

      if (data.warning) {
        toast(data.warning, {
          icon: '⚠️',
        });
      } else {
        toast.success('CSV file uploaded');
      }
    },
    onError: (error: unknown) => {
      handleError(error);
    },
  });

  // Consolidated clearSoilingRatioSelections function
  const clearSoilingRatioSelections = (ratioKey: string, fileUidToRemove?: string) => {
    const existingData = getFormDataItem<SoilingRatioSelections | undefined>(getValues(), ratioKey);
    if (existingData) {
      // If fileUidToRemove is provided, filter out sensor_files that reference it
      // Otherwise, clear all selections
      const filteredSensorFiles = fileUidToRemove ? existingData.sensor_files?.filter((sf) => sf.uid !== fileUidToRemove) || [] : [];

      // If there are no more sensor files with selections, clear everything
      if (filteredSensorFiles.length === 0 || !filteredSensorFiles.some((sf) => (sf.selected?.length ?? 0) > 0)) {
        updateFormValue(ratioKey, {
          sensor_files: [],
          hsu_selected: false,
        });
      } else {
        updateFormValue(ratioKey, {
          ...existingData,
          sensor_files: filteredSensorFiles,
        });
      }
    }
  };

  const postSoilingSensorData = useQueryMutation({
    mutationFn: ({ file, params }: UploadVariables): Promise<SoilingSensorUploadResponse> => postSoilingSensorDataFileAPI(file, params),
    onSuccess: (data, variables) => {
      const { field, name, params } = variables;
      // Extract file_uid from response and update the block's file_uid field
      // The response should contain the UID directly or in a file_uid field
      const fileUid = typeof data === 'string' ? data : data?.file_uid; // Handle both cases: {file_uid: "xxx"} or just "xxx"

      if (fileUid) {
        // Determine the block name from the field name
        const blockName = name.replace('.pattern', '');
        const fileUidFieldName = `${blockName}.file_uid`;

        // Get the old file_uid before updating
        const oldFileUid = getFormDataItem<string | undefined>(getValues(), fileUidFieldName);

        updateFormValue(fileUidFieldName, fileUid);

        // Use the consolidated function
        clearSoilingRatioSelections('soiling_ratio_operational', oldFileUid);
        clearSoilingRatioSelections('soiling_ratio_operational_ex_post', oldFileUid);

        // Fetch the actual data using the file_uid
        if (params.project_id) {
          // Determine sensor type from the block, default to rde300
          const sensorType = getFormDataItem<string | undefined>(getValues(), `${blockName}.type`) || 'rde300';
          postSensorDataAPI({
            file_uid: fileUid,
            file_type: sensorType,
            project_id: params.project_id,
          })
            .then((sensorData) => {
              // Set the chart data first, THEN flag the form as modified. The autosave snapshots the
              // sensor patterns (preservedPatternData) at save time; if we flagged modified before the
              // pattern arrived, the save/reload cycle would wipe the chart until a manual page reload.
              updateFormValue(name, sensorData);
              if (!modified) {
                setModified(true);
                setResults({});
              }
            })
            .catch((error) => {
              console.error('Error fetching sensor data:', error);

              // Clear the pattern data when there's an error to show empty graph instead of old data
              updateFormValue(name, null);

              handleError(error);
            });
        }
      }

      if (Object.prototype.hasOwnProperty.call(field, 'onChange')) {
        field.onChange?.(data);
      }

      if (typeof data !== 'string' && data.warning) {
        toast(data.warning, {
          icon: '⚠️',
        });
      } else {
        toast.success('Soiling sensor data uploaded');
      }
    },
    onError: (error) => {
      handleError(error);
    },
  });

  return (
    <FormProvider {...methods}>
      <FormContext.Provider
        value={{
          getValues,
          setFormData,
          getFormDataItem,
          updateDataValue,
          updateFormValue,
          results,
          setResults,
          handleChange,
          handleSpecialArrayFieldChange,
          clearSoilingRatioSelections,
        }}
      >
        {children}
      </FormContext.Provider>
    </FormProvider>
  );
};

export const useFormContext = (): FormContextType => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormContextProvider');
  }
  return context;
};

export default FormContextProvider;
